import { car_log_entries, RepairAnalysisData, RepairAnalysisFactor } from './supabase';
import { getFactorConfig, FUEL_TYPES_CONFIG, VERDICT_RECOMMENDATIONS } from './repairAnalysisConfig';
import { estimateMileageAtDate } from './mileageUtils';

export const calculateRepairAnalysis = (
  car: any,
  logEntries: car_log_entries[],
  overrides: {
    plannedRepairCost?: number,
    yearlyKm?: number,
    yearlyRepairCost?: number,
    carValue?: number,
    q1?: number,
    q2?: number,
    q3?: number,
    rust?: string,
    accident?: string,
    repaint?: string,
    unresolved?: string,
    ownershipYears?: number,
    serviceVisits?: number,
    maintenanceDiscipline?: number,
    diyRepairs?: number,
    familyTripPriority?: number,
    nextCarPlan?: string,
    nextCarBudget?: number,
    language?: string,
    currency?: string
  } = {}
): RepairAnalysisData => {
  const now = new Date();
  const language = overrides.language || 'en';
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setFullYear(now.getFullYear() - 1);
  
  const currencyMapper: Record<string, string> = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'PLN': 'zł',
    'SEK': 'kr',
    'NOK': 'kr',
    'CHF': 'CHF'
  };

  const currencySymbol = currencyMapper[overrides.currency || car.currency] || '€';
  const distUnit = car.distance_unit || 'km';
  const unit = distUnit === 'miles' ? (language === 'lv' ? 'jūdzes' : 'miles') : 'km';
  const unitFull = distUnit === 'miles' ? (language === 'lv' ? 'jūdžu' : 'miles') : 'km';
  
  const purchaseDate = car.bought_at ? new Date(car.bought_at) : now;
  const calculatedYears = Math.max(0.1, (now.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24 * 365));
  const ownershipYears = (overrides.ownershipYears !== undefined && overrides.ownershipYears >= 0) ? overrides.ownershipYears : calculatedYears;

  // Sorting and basic processing
  const allExpenses = logEntries.flatMap(le => (le.expenses || []).map((e: any) => ({ ...e, date: le.date, log_entry_id: le.id })));
  
  // Categorization and Weighting
  const isRepair = (t: string) => t === 'repairs' || t === 'service_repair';
  const isMaintenance = (t: string) => t === 'maintenance' || t === 'service_maintenance';
  
  const getWeight = (t: string) => {
    if (isRepair(t)) return 1.0;
    if (isMaintenance(t)) return 0.5;
    return 0.0;
  };

  const weightedExpenses = allExpenses.map(e => ({ ...e, weight: getWeight(e.type) }));
  const repairExpensesThisYear = weightedExpenses.filter(e => e.weight > 0 && new Date(e.date) >= twelveMonthsAgo);
  const repairExpensesLastYear = weightedExpenses.filter(e => e.weight > 0 && new Date(e.date) < twelveMonthsAgo && new Date(e.date) >= new Date(twelveMonthsAgo.getTime() - 365 * 24 * 60 * 60 * 1000));

  const repairCostThisYear = repairExpensesThisYear.reduce((sum, e) => sum + ((e.amount || 0) * e.weight), 0);
  const repairCostLastYear = repairExpensesLastYear.reduce((sum, e) => sum + ((e.amount || 0) * e.weight), 0);
  const repairCostAllTimeWeighted = weightedExpenses.filter(e => e.weight > 0).reduce((sum, e) => sum + ((e.amount || 0) * e.weight), 0);
  const totalCostThisYear = allExpenses.filter(e => new Date(e.date) >= twelveMonthsAgo).reduce((sum, e) => sum + (e.amount || 0), 0);

  // Determine usage (mileage)
  const mileageLogs = logEntries
    .filter(e => e.mileage !== null && e.mileage !== undefined)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  let estimatedYearlyKM = Number(car.estimated_yearly_km) || 0;
  
  if (mileageLogs.length >= 2) {
    const latest = mileageLogs[0];
    const bestLog = mileageLogs.find(l => {
      const diff = new Date(latest.date).getTime() - new Date(l.date).getTime();
      return diff >= 300 * 24 * 60 * 60 * 1000 && diff <= 400 * 24 * 60 * 60 * 1000;
    }) || mileageLogs[mileageLogs.length - 1];

    const days = (new Date(latest.date).getTime() - new Date(bestLog.date).getTime()) / (1000 * 60 * 60 * 24);
    const mDiff = (Number(latest.mileage) || 0) - (Number(bestLog.mileage) || 0);
    
    if (days >= 30 && mDiff > 0) {
      estimatedYearlyKM = Math.round((mDiff / days) * 365);
    }
  }

  // If still no rate, try purchase data vs now
  if (!estimatedYearlyKM || estimatedYearlyKM <= 0) {
     const currentEst = estimateMileageAtDate(car.purchase_mileage, car.bought_at, logEntries, car.estimated_yearly_km, now);
     const pMileage = Number(car.purchase_mileage);
     if (pMileage > 0 && currentEst > pMileage && car.bought_at) {
        const pDays = (now.getTime() - new Date(car.bought_at).getTime()) / (1000 * 60 * 60 * 24);
        if (pDays >= 30) {
           estimatedYearlyKM = Math.round(((currentEst - pMileage) / pDays) * 365);
        }
     }
  }

  if (!estimatedYearlyKM || estimatedYearlyKM <= 0) {
    estimatedYearlyKM = 15000;
  }

  const firstDataDate = logEntries.length > 0 ? new Date(logEntries[logEntries.length - 1].date) : purchaseDate;
  const dataAgeYears = Math.max(0.1, (now.getTime() - firstDataDate.getTime()) / (1000 * 60 * 60 * 24 * 365));
  const costEffectivePeriod = Math.min(1.0, dataAgeYears);
  const lifetimeAnnualizer = Math.max(0.5, dataAgeYears);

  const finalYearlyKm = overrides.yearlyKm || estimatedYearlyKM;
  const baseRepairCost = repairCostAllTimeWeighted > 0 ? (repairCostAllTimeWeighted / lifetimeAnnualizer) : 500;
  const projectedRepair = overrides.yearlyRepairCost !== undefined ? overrides.yearlyRepairCost : baseRepairCost;
  const currentMarketValue = overrides.carValue || car.current_market_value || 0;

  const normalizeToKm = (val: number) => distUnit === 'miles' ? val * 1.60934 : val;
  const estimatedNow = estimateMileageAtDate(car.purchase_mileage, car.bought_at, logEntries, car.estimated_yearly_km, now);
  const currentMileage = (estimatedNow === 0 && car.mileage > 0) ? car.mileage : estimatedNow;
  const normalizedLatestMileage = normalizeToKm(currentMileage);
  const carAge = car.year ? now.getFullYear() - car.year : 0;
  
  let rawScore = 100;
  const factors: RepairAnalysisFactor[] = [];
  
  const addFactor = (
    id: string, 
    type: 'positive' | 'negative' | 'neutral', 
    impact: number, 
    value: string, 
    adviceVariant?: 'high' | 'low' | 'optimal' | 'critical',
    opts: any = {}
  ) => {
    const config = getFactorConfig(id);
    if (!config) return;

    const hasAdviceVariant = adviceVariant && (config.advice as any)?.[adviceVariant];
    const adviceKey = hasAdviceVariant ? `improve_${id}_${adviceVariant}` : undefined;

    const details = {
      ...opts.details,
      val: value,
      km: value,
      mileage: value,
      unit,
      unitFull,
      currencySymbol
    };

    factors.push({ 
      id, 
      name: id,
      description: `${id}Desc`, 
      type, 
      impact, 
      value, 
      group: config.group, 
      adviceKey,
      ...opts,
      details
    });
    rawScore += impact;
  };

  // 1. REPAIR FREQUENCY
  const visitsMap: Record<string, number> = {};
  repairExpensesThisYear.forEach(e => {
    const key = e.log_entry_id || e.date;
    visitsMap[key] = Math.max(visitsMap[key] || 0, e.weight);
  });
  const weightedVisitCount = Object.values(visitsMap).reduce((sum, w) => sum + w, 0);
  const repairVisitsCount = overrides.serviceVisits !== undefined ? overrides.serviceVisits : weightedVisitCount;
  
  const rfCfg = getFactorConfig('repairFreq');
  const rfTh = (rfCfg?.logic?.thresholds || {}) as any;
  const rfImp = (rfCfg?.logic?.impacts || {}) as any;
  let rfImpact = 0;
  if (repairVisitsCount >= (rfTh.critical || 5)) rfImpact = rfImp.critical || -25;
  else if (repairVisitsCount >= (rfTh.high || 3)) rfImpact = rfImp.high || -10;
  addFactor('repairFreq', rfImpact < 0 ? 'negative' : 'neutral', rfImpact, repairVisitsCount.toFixed(1).replace('.0', ''), rfImpact < 0 ? 'high' : undefined, { details: { visits: repairVisitsCount.toFixed(1).replace('.0', '') }});

  // 2. MILEAGE
  const mCfg = getFactorConfig('mileage');
  const mTh = (mCfg?.logic?.thresholds || {}) as any;
  const mImp = (mCfg?.logic?.impacts || {}) as any;
  let mImpact = 0;
  if (normalizedLatestMileage >= (mTh.extreme || 350000)) mImpact = mImp.extreme || -25;
  else if (normalizedLatestMileage >= (mTh.high || 200000)) mImpact = mImp.high || -10;
  addFactor('mileage', mImpact < 0 ? 'negative' : 'neutral', mImpact, Math.round(currentMileage).toLocaleString(), mImpact < -20 ? 'critical' : (mImpact < 0 ? 'high' : undefined));

  // 3. YEARLY MILEAGE
  const yCfg = getFactorConfig('yearlyUsage');
  const yTh = (yCfg?.logic?.thresholds || {}) as any;
  const yImp = (yCfg?.logic?.impacts || {}) as any;
  const normalizedYearlyKm = normalizeToKm(finalYearlyKm);
  let ykImpact = 0;
  let ykVariant: 'high' | 'low' | undefined;
  if (normalizedYearlyKm > (yTh.high || 18000)) { ykImpact = yImp.high || -10; ykVariant = 'high'; }
  else if (normalizedYearlyKm < (yTh.low || 4000)) { ykImpact = yImp.low || -5; ykVariant = 'low'; }
  addFactor('yearlyUsage', ykImpact < 0 ? 'negative' : 'neutral', ykImpact, finalYearlyKm.toLocaleString(), ykVariant);

  // 4. REPAIR VS VALUE
  const rvImpacts = (getFactorConfig('repairVsValue')?.logic?.impacts || {}) as any;
  const repairToValueRatio = currentMarketValue > 0 ? (projectedRepair / currentMarketValue) * 100 : 0;
  let rvImpact = 0;
  if (repairToValueRatio >= 50) rvImpact = rvImpacts.highRisk || -40;
  else if (repairToValueRatio >= 20) rvImpact = rvImpacts.risky || -20;
  addFactor('repairVsValue', rvImpact < 0 ? 'negative' : 'neutral', rvImpact, `${repairToValueRatio.toFixed(0)}%`, rvImpact < 0 ? 'high' : undefined, { details: { ratio: repairToValueRatio.toFixed(0), cost: projectedRepair.toFixed(0), value: currentMarketValue.toFixed(0) }});

  // 5. COST PER DISTANCE
  const estNow = currentMileage;
  const est12m = estimateMileageAtDate(car.purchase_mileage, car.bought_at, logEntries, car.estimated_yearly_km, twelveMonthsAgo);
  
  // For the analysis view, we want to see CPK based on the PROJECTED (simulated) distance,
  // so that the UI reacts when the user moves the slider.
  const rolling12mDist = finalYearlyKm; 
  
  // Use projectedRepair (user simulation) instead of strict historical totals for CPK
  const annualTotalCost = projectedRepair; 
  const cpkValue = rolling12mDist > 0 ? annualTotalCost / rolling12mDist : 0;
  const normalizedCpk = rolling12mDist > 0 ? annualTotalCost / normalizeToKm(rolling12mDist) : 0;

  const cpkImp = (getFactorConfig('costPerKm')?.logic?.impacts || {}) as any;
  let cpkImpact = 0;
  let cpkVariant: 'high' | 'optimal' | undefined;
  if (normalizedCpk >= 0.3) { cpkImpact = cpkImp.high || -15; cpkVariant = 'high'; }
  else if (normalizedCpk <= 0.05 && normalizedCpk > 0) { cpkImpact = cpkImp.optimal || 5; cpkVariant = 'optimal'; }
  addFactor('costPerKm', cpkImpact < 0 ? 'negative' : (cpkImpact > 0 ? 'positive' : 'neutral'), cpkImpact, cpkValue.toFixed(2), cpkVariant, { details: { cpk: cpkValue.toFixed(2) }});

  // 6. TECHNICAL
  const rust = overrides.rust || car.rust_level || 'none';
  if (rust !== 'none') {
    const imps = (getFactorConfig('rust')?.logic?.impacts || {}) as any;
    addFactor('rust', 'negative', imps[rust] || -15, rust, 'high', { details: { status: rust }});
  }
  const unresolved = overrides.unresolved || 'none';
  if (unresolved !== 'none') {
    const imps = (getFactorConfig('unresolved')?.logic?.impacts || {}) as any;
    addFactor('unresolved', 'negative', imps[unresolved] || -15, unresolved, 'high', { details: { status: unresolved }});
  }

  const accident = overrides.accident || 'none';
  if (accident !== 'none') {
    const imps = (getFactorConfig('accident')?.logic?.impacts || {}) as any;
    addFactor('accident', 'negative', imps[accident] || -10, accident, 'high', { details: { status: accident }});
  }

  const repaint = overrides.repaint || 'none';
  if (repaint !== 'none') {
    const imps = (getFactorConfig('repaint')?.logic?.impacts || {}) as any;
    addFactor('repaint', 'negative', imps[repaint] || -5, repaint, 'low', { details: { status: repaint }});
  }

  // 7. POWERTRAIN & AGING RISKS
  const ptCfg = getFactorConfig('powertrainRisk');
  const ptTh = (ptCfg?.logic?.thresholds || {}) as any;
  const ptImp = (ptCfg?.logic?.impacts || {}) as any;
  let ptImpact = 0;
  if (car.fuel_type === 'diesel') {
    if (normalizedLatestMileage > (ptTh.dieselCriticalKm || 250000)) ptImpact = ptImp.critical || -20;
    else if (normalizedLatestMileage > (ptTh.dieselHighKm || 180000)) ptImpact = ptImp.high || -10;
  } else if (car.fuel_type === 'petrol') {
    if (normalizedLatestMileage > (ptTh.petrolCriticalKm || 300000)) ptImpact = ptImp.critical || -20;
    else if (normalizedLatestMileage > (ptTh.petrolHighKm || 200000)) ptImpact = ptImp.high || -10;
  }
  if (ptImpact < 0) addFactor('powertrainRisk', 'negative', ptImpact, car.fuel_type, 'high', { details: { fuel: car.fuel_type }});

  const agingCfg = getFactorConfig('agingRisk');
  const agingTh = (agingCfg?.logic?.thresholds || {}) as any;
  const agingImp = (agingCfg?.logic?.impacts || {}) as any;
  let agingImpact = 0;
  if (carAge > (agingTh.criticalAge || 20)) agingImpact = agingImp.critical || -20;
  else if (carAge > (agingTh.highAge || 15)) agingImpact = agingImp.high || -10;
  if (agingImpact < 0) addFactor('agingRisk', 'negative', agingImpact, String(carAge), 'high', { details: { years: carAge }});

  // 8. NEW CAR COMPARISON (V2)
  if (overrides.nextCarPlan && overrides.nextCarPlan !== 'none') {
    const nextBudget = overrides.nextCarBudget || 15000;
    const plannedRepair = overrides.plannedRepairCost || 0;
    const repairBurden = Math.max(plannedRepair, projectedRepair);
    const priceGap = Math.max(100, nextBudget - currentMarketValue); // Minimum 100 to avoid div by zero
    
    const isNew = overrides.nextCarPlan === 'new';
    const ratio = repairBurden / priceGap;
    const negTh = isNew ? 0.5 : 0.7;
    const posTh = isNew ? 0.2 : 0.3;
    
    let compType: 'positive' | 'negative' | 'neutral' = 'neutral';
    let compImpact = 0;
    let compVariant: 'high' | 'low' | undefined;
    let verdictText = '';

    if (language === 'lv') {
      verdictText = ratio > negTh ? 'Remonts ir pārāk dārgs.' : (ratio < posTh ? 'Remonts ir ļoti izdevīgs.' : 'Remonts ir loģisks.');
    } else {
      verdictText = ratio > negTh ? 'Repair is too expensive.' : (ratio < posTh ? 'Repair is a good investment.' : 'Repair is sensible.');
    }

    if (ratio > negTh) {
      compType = 'negative';
      compImpact = isNew ? -15 : -10;
      compVariant = 'high';
    } else if (ratio < posTh && repairBurden > 0) {
      compType = 'positive';
      compImpact = 10;
      compVariant = 'low';
    }

    const planLabel = isNew ? (language === 'lv' ? 'jaunu auto' : 'new car') : (language === 'lv' ? 'lietotu auto' : 'used car');

    addFactor('replacement_comparison', compType, compImpact, `${currencySymbol}${repairBurden.toFixed(0)}`, compVariant, {
      details: { 
        repairCost: `${currencySymbol}${repairBurden.toFixed(0)}`,
        gap: `${currencySymbol}${priceGap.toFixed(0)}`,
        nextCarPlan: planLabel,
        verdictText
      }
    });
  }

  // 8. PERSONAL
  const mDisc = overrides.maintenanceDiscipline || 3;
  let mDiscImpact = 0;
  if (mDisc >= 5) mDiscImpact = 12;
  else if (mDisc >= 4) mDiscImpact = 6;
  else if (mDisc === 2) mDiscImpact = -10;
  else if (mDisc === 1) mDiscImpact = -25;
  addFactor('maintenance_discipline', mDiscImpact >= 0 ? (mDiscImpact > 0 ? 'positive' : 'neutral') : 'negative', mDiscImpact, `${mDisc}/5`, mDiscImpact < -20 ? 'critical' : (mDiscImpact < 0 ? 'high' : undefined), { details: { score: mDisc }});

  const diy = overrides.diyRepairs || 1;
  let diyImpact = Math.max(0, (diy - 1) * 3);
  addFactor('diy_repairs', diyImpact > 0 ? 'positive' : 'neutral', diyImpact, `${diy}/5`, undefined, { details: { score: diy }});

  const fCfg = getFactorConfig('fatigue');
  const fTh = (fCfg?.logic?.thresholds || {}) as any;
  const fImp = (fCfg?.logic?.impacts || {}) as any;
  let fImpact = 0;
  if (ownershipYears > (fTh.fatigueExtreme || 8)) fImpact = fImp.extremeFatigue || -15;
  else if (ownershipYears > (fTh.fatigueMin || 5)) fImpact = fImp.fatigue || -5;
  else if (ownershipYears < (fTh.biasMax || 1)) fImpact = fImp.initialBias || 5;
  if (fImpact !== 0) addFactor('fatigue', fImpact > 0 ? 'positive' : 'negative', fImpact, ownershipYears.toFixed(1), fImpact > 0 ? 'low' : 'high', { details: { years: ownershipYears.toFixed(1) }});

  const pScore = overrides.q1 !== undefined ? overrides.q1 : (car.practical_fit_score || 3);
  if (pScore !== 3) addFactor('practical_fit', pScore > 3 ? 'positive' : 'negative', (pScore - 3) * 5, `${pScore}/5`, pScore < 3 ? 'low' : undefined, { details: { score: pScore } });

  const sScore = overrides.q2 !== undefined ? overrides.q2 : (car.emotional_satisfaction_score || 3);
  if (sScore !== 3) addFactor('satisfaction', sScore > 3 ? 'positive' : 'negative', (sScore - 3) * 5, `${sScore}/5`, sScore < 3 ? 'low' : undefined, { details: { score: sScore } });

  const aScore = overrides.q3 !== undefined ? overrides.q3 : (car.reliability_anxiety_score || 3);
  const fPriority = overrides.familyTripPriority || 3;

  if (aScore !== 3) {
    let aImpact = (3 - aScore) * 5;
    if (aImpact < 0 && fPriority >= 4) {
      aImpact = Math.round(aImpact * (fPriority === 5 ? 1.5 : 1.25));
    }
    addFactor('reliability_anxiety', aImpact > 0 ? 'positive' : 'negative', aImpact, `${aScore}/5`, aImpact < 0 ? 'high' : undefined, { details: { score: aScore } });
  }

  // Family Priority Risk Multiplier (If high priority + other negative factors exist)
  if (fPriority >= 4) {
    const negativeImpacts = factors.filter(f => f.type === 'negative').reduce((sum, f) => sum + f.impact, 0);
    if (negativeImpacts < -30) {
      const fImpact = fPriority === 5 ? -15 : -8;
      addFactor('family_priority', 'negative', fImpact, `${fPriority}/5`, 'high', { details: { score: fPriority, riskLevel: Math.abs(negativeImpacts) } });
    } else {
      addFactor('family_priority', 'neutral', 0, `${fPriority}/5`);
    }
  }

  // 9. PLANNED REPAIRS (immediate burden)
  if (overrides.plannedRepairCost && overrides.plannedRepairCost > 0) {
    const pCost = overrides.plannedRepairCost;
    
    if (currentMarketValue > 0 && pCost >= currentMarketValue) {
       // Repair cost exceeds car value - zero health
       addFactor('planned', 'negative', -200, `${currencySymbol}${pCost}`, 'critical', { 
         details: { 
           amount: pCost, 
           message: language === 'lv' ? 'Remonta izmaksas pārsniedz auto vērtību!' : 'Repair cost exceeds car value!' 
         } 
       });
    } else {
       // -5 points per 100 EUR/USD, max -30
       const pImpact = Math.max(-30, -Math.floor(pCost / 100) * 5);
       addFactor('planned', 'negative', pImpact, `${currencySymbol}${pCost}`, 'high', { details: { amount: pCost } });
    }
  }

  const finalScore = Math.max(0, Math.min(100, Math.round(rawScore)));
  let verdict: 'excellent' | 'good' | 'average' | 'poor' | 'critical' = 'average';
  let recommendationKey = 'CONSIDER SELLING';

  if (finalScore >= 85) { verdict = 'excellent'; recommendationKey = 'KEEP'; }
  else if (finalScore >= 70) { verdict = 'good'; recommendationKey = 'KEEP'; }
  else if (finalScore >= 45) { verdict = 'average'; recommendationKey = 'CONSIDER SELLING'; }
  else if (finalScore >= 25) { verdict = 'poor'; recommendationKey = 'SELL'; }
  else { verdict = 'critical'; recommendationKey = 'SELL'; }

  const recommendation = (VERDICT_RECOMMENDATIONS[recommendationKey] as any)?.[language] || VERDICT_RECOMMENDATIONS[recommendationKey]?.en;

  const depreciation12m = currentMarketValue * 0.12;
  const totalInvested = (car.purchase_price || 0) + allExpenses.filter(e => e.type !== 'fuel' && e.type !== 'electricity').reduce((sum, e) => sum + (e.amount || 0), 0);
  const fuelTotal = allExpenses.filter(e => e.type === 'fuel' || e.type === 'electricity').reduce((sum, e) => sum + (e.amount || 0), 0);

  return {
    score: finalScore,
    verdict,
    recommendation,
    factors: factors.sort((a,b) => Math.abs(b.impact) - Math.abs(a.impact)),
    suggestedYearlyKM: Math.round(estimatedYearlyKM),
    suggestedYearlyRepairs: Math.round(baseRepairCost),
    totalInvested,
    fuelTotal,
    currentMarketValue,
    investmentGap: totalInvested - currentMarketValue,
    monthly_cost_current: (totalCostThisYear + depreciation12m) / 12,
    repairExpenses12m: repairCostThisYear
  };
};
